import type {DesignMeta, GraphicMeta, GraphicsMetaMapType, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import type {WizardUploadDesignsCallbacks, WizardUploadMediaGa4Callbacks} from '@Libraries/social-media-wizard-design-library.types';
import {MAX_UPLOADS_TO_DESIGNS} from '@Libraries/social-media-wizard-design-library.types';
import {getPostersPageIds} from '@Libraries/poster-library';
import {GA4EventName} from '@Libraries/ga-events';
import {noop} from '@Utils/general.util';

const DESIGN_GALLERY_BOTTOM_OFFSET = 250;

export const WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS: WizardUploadMediaGa4Callbacks = {
  onTriggered: (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CLICKED);
  },
  onPosterCreated: (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CREATED);
  },
};

export const getWizardUploadDesignsModalOpts = ({
  onDesignsCreated,
  onPosterCreationsStarted,
  onTriggered,
  onPosterCreated = noop,
}: WizardUploadDesignsCallbacks): CreateDesignFromMediaModalOpts => {
  return {
    allowMultipagePosterCreation: false,
    maxSelectionNumber: MAX_UPLOADS_TO_DESIGNS,
    shouldUseFastPendingImageUploadFlow: false,
    onAllPostersCreated: (posterHashedIds: PosterHashedId[]): void => {
      getDesignSelectionsForPosters(posterHashedIds)
        .then(onDesignsCreated)
        .catch((err: unknown) => {
          console.error('Error fetching poster page IDs:', err);
        });
    },
    onPosterCreated,
    onTriggered,
    onPosterCreationsStarted,
  };
};

const getDesignSelectionsForPosters = async (posterHashedIds: PosterHashedId[]): Promise<DesignSelection[]> => {
  const pageIdsMap = await getPostersPageIds(posterHashedIds);

  return Object.keys(pageIdsMap).flatMap((posterId): DesignSelection[] => {
    return pageIdsMap[posterId].map((pageId): DesignSelection => {
      return {posterHashedId: posterId, pageHashedId: pageId};
    });
  });
};

export const getGraphicMeta = async ({posterHashedId, pageHashedId}: DesignSelection): Promise<GraphicMeta> => {
  const data = (await window.PMW.writeLocal('socialmedia/getDesignMeta', {
    posterId: posterHashedId,
    pageId: pageHashedId,
  })) as DesignMeta;

  return {graphicURL: data.previewUrl, isVideo: data.isVideo, name: data.designName} as GraphicMeta;
};

export const getFilteredDesigns = (designs: DesignSelection[], max: number): DesignSelection[] => {
  const sanitizedArray = designs.filter((design) => design.posterHashedId && design.pageHashedId);
  if (sanitizedArray.length > max) {
    sanitizedArray.splice(max);
  }

  return sanitizedArray;
};

export const isSingleDesignSelected = (designs: DesignSelection[]): boolean => {
  return designs.length === 1;
};

export const getKeyForPublishParams = (accountId: SocialMediaAccountId): string => {
  return `publish-params-${accountId}`;
};

export const getDesignGalleryHeightForSelectDesignStep = (): string => {
  return `${window.innerHeight - DESIGN_GALLERY_BOTTOM_OFFSET}px`;
};

export const getSanitizedEmphasizedPlatform = (emphasizedPlatform: SocialPlatform | undefined): SocialPlatform | undefined => {
  return emphasizedPlatform ?? undefined;
};

export const buildGraphicsMetaMap = (
  previewUrls: string[],
  designsIsVideo: boolean[],
  designNames: string[],
  designSelectionInformation: DesignSelection[] = []
): GraphicsMetaMapType => {
  const graphicsMetaMap: GraphicsMetaMapType = {};

  for (let i = 0; i < designSelectionInformation.length; i++) {
    const {posterHashedId, pageHashedId} = designSelectionInformation[i];

    graphicsMetaMap[posterHashedId] ??= {};
    graphicsMetaMap[posterHashedId][pageHashedId] = {
      isVideo: designsIsVideo[i],
      graphicURL: previewUrls[i],
      name: designNames[i],
      order: i,
    };
  }

  return graphicsMetaMap;
};

export const getWizardURLForEmphasizedPlatform = (platform: SocialPlatform, idTemplate?: string): string => {
  const getParamKey = 'emphasizedPlatform';

  if (idTemplate) {
    return window.PMW.util.site_url(`socialmedia/post/template/${idTemplate}?${getParamKey}=${platform}`);
  }

  return window.PMW.util.site_url(`socialmedia/post?${getParamKey}=${platform}`);
};
